import { CDP_FLAT_OUTCOME_PARAM_KEYS } from './screenerOutcomeParams.constants';

export type ScreenerOutcomeParam = {
  key: string;
  value: string;
  isPII?: boolean;
};

export function combineScreenerOutcomeParams(
  ...sources: (ScreenerOutcomeParam[] | null | undefined)[]
): ScreenerOutcomeParam[] {
  const combinedOutcomeParams: ScreenerOutcomeParam[] = [];
  const seenKeys = new Set<string>();

  for (const source of sources) {
    source?.forEach((param) => {
      if (param?.key && param.value != null && param.value !== '' && !seenKeys.has(param.key)) {
        combinedOutcomeParams.push(param);
        seenKeys.add(param.key);
      }
    });
  }

  return combinedOutcomeParams;
}

export function hasCdpPrepopulateData(
  cdpData:
    | { screenerOutcomeParams?: ScreenerOutcomeParam[] | null; [key: string]: unknown }
    | null
    | undefined
): boolean {
  if (!cdpData) {
    return false;
  }

  if (cdpData.screenerOutcomeParams?.length) {
    return true;
  }

  return CDP_FLAT_OUTCOME_PARAM_KEYS.some((key) => cdpData[key] != null && cdpData[key] !== '');
}

function getFlatCdpValue(
  cdpData: { [key: string]: unknown } | null | undefined,
  key: string | string[]
): string | undefined {
  const keys = Array.isArray(key) ? key : [key];
  for (const paramKey of keys) {
    const flatValue = cdpData?.[paramKey];
    if (flatValue != null && flatValue !== '') {
      return String(flatValue);
    }
  }

  return undefined;
}

export function getScreenerOutcomeParamValue(
  screenerOutcomeParams: ScreenerOutcomeParam[] | null | undefined,
  key: string | string[]
): string | undefined {
  if (!screenerOutcomeParams?.length) {
    return undefined;
  }

  const keys = Array.isArray(key) ? key : [key];
  for (const paramKey of keys) {
    const match = screenerOutcomeParams.find((param) => param.key === paramKey);
    if (match?.value != null && match.value !== '') {
      return match.value;
    }
  }

  return undefined;
}

export function getPrepopulateValueFromSources(
  screenerOutcomeParameter: ScreenerOutcomeParam[] | null | undefined,
  cdpData:
    | { screenerOutcomeParams?: ScreenerOutcomeParam[] | null; [key: string]: unknown }
    | null
    | undefined,
  key: string | string[]
): string | undefined {
  const fromRedux = getScreenerOutcomeParamValue(screenerOutcomeParameter, key);
  if (fromRedux != null && fromRedux !== '') {
    return fromRedux;
  }

  if (!hasCdpPrepopulateData(cdpData)) {
    return undefined;
  }

  const fromCdpParams = getScreenerOutcomeParamValue(cdpData?.screenerOutcomeParams, key);
  if (fromCdpParams != null && fromCdpParams !== '') {
    return fromCdpParams;
  }

  return getFlatCdpValue(cdpData, key);
}

export function buildCombinedOutcomeParams(
  screenerOutcomeParameter: ScreenerOutcomeParam[] | null | undefined,
  cdpData:
    | { screenerOutcomeParams?: ScreenerOutcomeParam[] | null; [key: string]: unknown }
    | null
    | undefined
): ScreenerOutcomeParam[] {
  return combineScreenerOutcomeParams(
    screenerOutcomeParameter,
    hasCdpPrepopulateData(cdpData) ? cdpData?.screenerOutcomeParams : undefined,
    hasCdpPrepopulateData(cdpData) ? flatCdpFieldsToOutcomeParams(cdpData) : undefined
  );
}

function flatCdpFieldsToOutcomeParams(
  cdpData: { [key: string]: unknown } | null | undefined
): ScreenerOutcomeParam[] {
  if (!cdpData) {
    return [];
  }

  return CDP_FLAT_OUTCOME_PARAM_KEYS.reduce<ScreenerOutcomeParam[]>((params, key) => {
    const value = cdpData[key];
    if (value != null && value !== '') {
      params.push({ key, value: String(value) });
    }
    return params;
  }, []);
}

export function getPrepopulateValue(
  combinedOutcomeParams: ScreenerOutcomeParam[],
  key: string | string[]
): string | undefined {
  return getScreenerOutcomeParamValue(combinedOutcomeParams, key);
}
